import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, PlugZap } from "lucide-react";
import { saveCfCredentials, verifyCf } from "@/lib/videoApi";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Values are sent once over HTTPS to the server, checked with Cloudflare, stored server-side, and never shown again.
export function CloudflareCredentials({ configured, onSaved }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(!configured);
  const [acc, setAcc] = useState("");
  const [tok, setTok] = useState("");
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const save = async () => {
    setBusy("save"); setResult(null);
    try { const r = await saveCfCredentials({ account_id: acc, api_token: tok }); setAcc(""); setTok(""); setOpen(false); setResult(r); onSaved(r); toast.success("Cloudflare Stream connected"); }
    catch (e) { setResult({ ok: false, detail: e.message }); } finally { setBusy(""); }
  };
  const verify = async () => { setBusy("verify"); try { setResult(await verifyCf()); } catch (e) { setResult({ ok: false, detail: e.message }); } finally { setBusy(""); } };
  const isSuper = user?.role === "super_admin";
  return (
    <div data-testid="cf-credentials" className="mt-3 rounded-lg border border-stone-200 bg-stone-50 p-3 space-y-3">
      <div className="flex flex-wrap gap-2">
        {configured && <Button variant="outline" data-testid="cf-verify" disabled={!!busy} onClick={verify} className="h-11 rounded-full">{busy === "verify" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PlugZap className="h-4 w-4 mr-1.5" />Test Connection</>}</Button>}
        {isSuper && configured && !open && <Button variant="outline" data-testid="cf-replace" onClick={() => setOpen(true)} className="h-11 rounded-full"><KeyRound className="h-4 w-4 mr-1.5" />Replace Credentials</Button>}
      </div>
      {result && <p data-testid="cf-result" role="status" className={`text-sm ${result.ok ? "text-emerald-800" : "text-red-700"}`}>{result.detail}</p>}
      {open && (isSuper ? (
        <form data-testid="cf-form" onSubmit={(e) => { e.preventDefault(); save(); }} className="space-y-3" autoComplete="off">
          <p className="text-xs text-stone-600">Enter your Cloudflare details here (not in chat). They're checked with Cloudflare, saved as private server settings, and never displayed again.</p>
          <label className="block text-xs font-semibold text-stone-600">CLOUDFLARE_ACCOUNT_ID
            <Input data-testid="cf-account-id" value={acc} onChange={(e) => setAcc(e.target.value)} autoComplete="off" spellCheck={false} className="mt-1 h-11 font-mono text-base" /></label>
          <label className="block text-xs font-semibold text-stone-600">CLOUDFLARE_API_TOKEN
            <Input data-testid="cf-api-token" type="password" value={tok} onChange={(e) => setTok(e.target.value)} autoComplete="new-password" spellCheck={false} className="mt-1 h-11 font-mono text-base" /></label>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" data-testid="cf-save" disabled={!!busy || !acc.trim() || !tok.trim()} className="h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Save"}</Button>
            {configured && <Button type="button" variant="outline" onClick={() => setOpen(false)} className="h-11 rounded-full">Cancel</Button>}
          </div>
        </form>
      ) : <p className="text-xs text-stone-600">Only the owner (super admin) can enter Cloudflare credentials.</p>)}
    </div>
  );
}
